import { googleOAuthLoginApi } from '~/api/auth';
import { useAuth } from '~/composables/useAuth';
import { useDeviceId } from '~/composables/useDeviceId';
import { useToastStore } from '~/stores/toast';
import type { ApiErrorWithStatus } from '~/types/api';
import { useLocalePath } from '#i18n';

/**
 * Google OAuth 回調的共用流程
 *
 * 「用 Google 帶回的 guest_user_uuid + 邀請碼完成註冊並登入」這件事有三個入口：
 *   - /auth/callback              Google 直接帶著兩個參數回來
 *   - /login                      middleware 把帶參數的請求導過來
 *   - InviteCodeModal             只帶回 guest_user_uuid，由使用者手動補邀請碼
 *
 * 三個入口過去各有一份實作，device_id、失敗後的 query 保留、導向目標都不一致。
 * 這裡是唯一的一份。
 */

/** 失敗時的行為 */
type FailureBehavior =
  /** 導回註冊頁，並保留 OAuth 流程需要的 query（適合中繼頁） */
  | 'redirect-to-signup'
  /** 留在原地，讓使用者修正後重試（適合 Modal） */
  | 'stay';

interface CompleteGuestSignupOptions {
  guestUserUuid: string;
  inviteCode: string;
  /**
   * 失敗時的行為，預設導回註冊頁
   */
  onFailure?: FailureBehavior;
  /**
   * 後端沒有回傳 message 時使用的錯誤文案
   */
  fallbackErrorMessage?: string;
}

/** 交接用的 server route（見 server/routes/_auth/oauth-handoff.post.ts） */
const OAUTH_HANDOFF_ENDPOINT = '/_auth/oauth-handoff';

/** OAuth 流程中需要跨頁保留的 query 參數 */
const RELEVANT_QUERY_KEYS = [
  'err_code',
  'guest_user_uuid',
  'invite_code',
  'email',
  'is_2fa_enabled',
  'provisional_2fa_token',
] as const;

export const useGoogleOAuth = () => {
  const route = useRoute();
  const localePath = useLocalePath();
  const toastStore = useToastStore();
  const { login } = useAuth();
  const { getOrCreateDeviceId } = useDeviceId();

  /**
   * 從當前路由挑出 OAuth 流程需要保留的 query 參數
   * 只保留白名單內的鍵，避免把整包 query（可能含敏感值）帶著跑
   */
  const extractRelevantQueryParams = (): Record<string, string> => {
    const query: Record<string, string> = {};
    for (const key of RELEVANT_QUERY_KEYS) {
      const value = route.query[key];
      if (value !== undefined && value !== null) {
        query[key] = String(value);
      }
    }
    return query;
  };

  /**
   * 成功註冊後要帶去組織設定頁的 query
   * guest_user_uuid 與 invite_code 已經用掉了，不再往下帶
   */
  const buildPostSignupQuery = (): string => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(route.query)) {
      if (value === undefined || value === null) continue;
      if (key === 'guest_user_uuid' || key === 'invite_code') continue;
      params.append(key, String(value));
    }
    return params.toString();
  };

  /**
   * 用 guest_user_uuid + 邀請碼完成註冊並登入
   *
   * 成功後導向組織設定頁（新註冊的使用者必然還沒有組織）。
   * 失敗時一律顯示錯誤訊息，是否導回註冊頁由 onFailure 決定。
   *
   * @returns 是否成功
   */
  const completeGuestSignup = async ({
    guestUserUuid,
    inviteCode,
    onFailure = 'redirect-to-signup',
    fallbackErrorMessage = 'signup failed, please try again',
  }: CompleteGuestSignupOptions): Promise<boolean> => {
    try {
      const response = await googleOAuthLoginApi({
        guest_user_uuid: guestUserUuid,
        invite_code: inviteCode,
        // 帶上 device_id，登出時才能只登出這台裝置
        device_id: getOrCreateDeviceId(),
      });

      const queryString = buildPostSignupQuery();
      const redirectPath = queryString
        ? `/organization/setup?${queryString}`
        : '/organization/setup';

      await login(response.token, response.refresh_token, localePath(redirectPath));
      return true;
    } catch (error) {
      console.error('Auto google OAuth login failed:', error);

      const apiError = error as ApiErrorWithStatus | Error;
      toastStore.error(
        'message' in apiError && apiError.message ? apiError.message : fallbackErrorMessage
      );

      if (onFailure === 'redirect-to-signup') {
        await navigateTo({
          path: localePath('/signup'),
          query: extractRelevantQueryParams(),
        });
      }
      return false;
    }
  };

  /**
   * 取回後端在 Google 登入時寫下的 refresh token（一次性）
   *
   * 一般 Google 登入流程裡，token 不走 query 而是走 cookie：後端 gateway 換到 token 後
   * 用 `Domain=.momentx.ai` + `HttpOnly` 寫下 `Authorization` / `RefreshToken`，才把
   * 瀏覽器導回 `/auth/callback`。前端的 cookie 已改名為 `xsenal_*`，讀自己的名字必然
   * 落空，所以要跟 server route 要——那兩份帶 HttpOnly，只有 server 端讀得到。
   *
   * 讀完 server route 會立刻把它們刪掉，因此這個值只能拿一次，拿到就要用掉。
   *
   * @returns refresh token；沒有交接可拿或請求失敗時回空字串
   */
  const consumeBackendHandoff = async (): Promise<string> => {
    try {
      const result = await $fetch<{ refresh_token: string | null }>(OAUTH_HANDOFF_ENDPOINT, {
        method: 'POST',
      });
      return result?.refresh_token ?? '';
    } catch (error) {
      // 交接拿不到就當作沒有登入紀錄，由呼叫端導回登入頁，不需要中斷整個流程
      console.warn('OAuth handoff failed:', error);
      return '';
    }
  };

  return {
    extractRelevantQueryParams,
    completeGuestSignup,
    consumeBackendHandoff,
  };
};
